// Trailer reads. Every function takes the Supabase client as a parameter and
// has no side effects.
import type { SupabaseClient } from "npm:@supabase/supabase-js@2";
import { findOnrampOrgIdByShortCode } from "../customer/queries.ts";
import { likeExact } from "./rules.ts";

export interface TrailerRef {
  id: string;
  trailer_id: string;
  scac: string | null;
}

/**
 * The tenant's trailer with this trailer_id, matched case-insensitively.
 * trailer_ids are unique per tenant (trailers_tenant_id_trailer_id_uniq); the
 * created_at order only makes the pick deterministic if that ever slips.
 */
export async function findTrailerByTrailerId(
  db: SupabaseClient,
  tenantId: string,
  trailerId: string,
): Promise<TrailerRef | null> {
  const { data, error } = await db.from("trailers").select("id, trailer_id, scac")
    .eq("tenant_id", tenantId).ilike("trailer_id", likeExact(trailerId.trim()))
    .order("created_at").limit(1);
  if (error) throw new Error(`trailers read failed: ${error.message}`);
  return (data as TrailerRef[] | null)?.[0] ?? null;
}

export interface TrailerOwningOrg {
  /** The trailer_id as stored (the match is case-insensitive). */
  trailer_id: string;
  /** Null when the trailer has no SCAC or its owner has no org in the tenant. */
  org_id: string | null;
}

/**
 * "Which org owns this trailer?" for EAPI's EPTI inspections: the trailer's
 * SCAC (trailers.scac) names the owning customer by its short code, and that
 * customer's org id in the tenant is the answer. No access check: EAPI
 * decides what its customers may see. Null when the tenant has no such
 * trailer; the endpoint decides what that means.
 */
export async function resolveTrailerOwningOrg(
  db: SupabaseClient,
  tenantId: string,
  trailerId: string,
): Promise<TrailerOwningOrg | null> {
  const trailer = await findTrailerByTrailerId(db, tenantId, trailerId);
  if (!trailer) return null;
  const orgId = trailer.scac ? await findOnrampOrgIdByShortCode(db, tenantId, trailer.scac) : null;
  return { trailer_id: trailer.trailer_id, org_id: orgId };
}
